<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { onMount } from 'svelte';
	//Services
	import { format } from 'src/services/i18n';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Helpers
	import { formatSnakeCaseToTitle } from 'src/helpers/utilities';
	import { real_id } from 'src/helpers/interface_helpers';
	import {
		convertSeconds,
		convertToSeconds,
		formatTwoDigits,
		formatTimeLabel,
		getTimeWheelTexts,
		getTimeWheelMsStep,
		getTimeWheelToggleText,
	} from 'src/helpers/timer_helpers';
	//Stores
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	//Design system components
	import { Button, Checkbox, toast, Container, TimeWheel } from '@Shelly/shelly-design-system';

	$: settingsStore = getDeviceSettings(id);
	export let device: Device;

	const id = real_id(device.id);

	let settingsPerChannel = $settingsStore[`switch:0`],
		subsettings = ['auto_on', 'auto_off'];
	let loading = { auto_on: false, auto_off: false };
	let min = 0,
		max = 2147483;

	let selectedHour = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMinute = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedSecond = { auto_on_delay: '00', auto_off_delay: '00' };
	let selectedMs = { auto_on_delay: undefined, auto_off_delay: undefined };
	let maxTimes = convertSeconds(max);

	$: infoText = `${$format('timer_info_description', {
		values: {
			min: min,
			max: formatTimeLabel(
				maxTimes.hours,
				maxTimes.minutes,
				maxTimes.seconds,
				maxTimes.milliseconds,
			),
		},
	})}`;

	$: checkBoxInfoText = {
		auto_on: `${$format('auto_on_description')} 
					<span class='text-primary text-bold'>
						${formatTimeLabel(
									selectedHour['auto_on_delay'],
									selectedMinute['auto_on_delay'],
									selectedSecond['auto_on_delay'],
									selectedMs['auto_on_delay'],
								)}
					</span>`,
		auto_off: `${$format('auto_off_description')}
					<span class='text-primary text-bold'>
						${formatTimeLabel(
									selectedHour['auto_off_delay'],
									selectedMinute['auto_off_delay'],
									selectedSecond['auto_off_delay'],
									selectedMs['auto_off_delay'],
								)}
					</span>`,
	};

	console.log('settingsPerChannel', settingsPerChannel);

	const method = `switch.SetConfig`;

	$: errors = {
		auto_on_error:
			convertToSeconds(
				selectedHour['auto_on_delay'],
				selectedMinute['auto_on_delay'],
				selectedSecond['auto_on_delay'],
				selectedMs['auto_on_delay'],
			) > max
				? infoText
				: '',
		auto_off_error:
			convertToSeconds(
				selectedHour['auto_off_delay'],
				selectedMinute['auto_off_delay'],
				selectedSecond['auto_off_delay'],
				selectedMs['auto_off_delay'],
			) > max
				? infoText
				: '',
	};

	onMount(() => {
		updateTimeValues('auto_on_delay');
		updateTimeValues('auto_off_delay');
	});

	const getTimeInSeconds = (type: 'auto_on_delay' | 'auto_off_delay') => {
		return convertToSeconds(
			selectedHour[type],
			selectedMinute[type],
			selectedSecond[type],
			selectedMs[type],
		);
	};

	const updateTimeValues = (type: 'auto_on_delay' | 'auto_off_delay') => {
		const { hours, minutes, seconds, milliseconds } = convertSeconds(settingsPerChannel[type] || 0);
		selectedHour[type] = formatTwoDigits(hours);
		selectedMinute[type] = formatTwoDigits(minutes);
		selectedSecond[type] = formatTwoDigits(seconds);
		selectedMs[type] = milliseconds ? formatTwoDigits(milliseconds) : undefined;
	};

	const set_auto = async (setting) => {
		loading[setting] = true;

		const secconds = getTimeInSeconds(`${setting}_delay` as any);
		const params = {
			id: 0,
			config: {
				[setting]: settingsPerChannel[setting],
				[setting + '_delay']: secconds,
			},
		};

		const res: SettingRequest = await gen2Request(device, method, params);
		if (res.isok) {
			if (!settingsPerChannel[setting]) {
				toast.open({
					type: 'success',
					title: $format('auto_turn_successfully_disable', {
						values: { auto_turn_state: $format(setting) },
					}),
				});
			} else {
				toast.open({
					type: 'success',
					title: `${$format(`${setting}_set_toast`)} ${formatTimeLabel(
						selectedHour[`${setting}_delay`],
						selectedMinute[`${setting}_delay`],
						selectedSecond[`${setting}_delay`],
						selectedMs[`${setting}_delay`],
					)}.`,
				});
			}
		} else {
			if (res?.errors?.message) {
				toast.open({ type: 'danger', title: res?.errors?.message });
			} else {
				toast.open({ type: 'danger', title: $format('timer_err_toast') });
			}
		}
		loading[setting] = false;
	};

	const handle_toggler = async (setting) => {
		settingsPerChannel[setting] = !settingsPerChannel[setting];
	};
</script>

{#each subsettings as setting}
	<Container
		backgroundColor={$showNewLayout ? 'color-0' : undefined}
		type={!$showNewLayout ? 'modalContainer' : undefined}
		padding="xs"
		class="mb-s border-color-transparent"
	>
		<Checkbox
			class="mb-s"
			size="m"
			info={checkBoxInfoText[setting]}
			checked={settingsPerChannel[setting]}
			on:click={() => handle_toggler(setting)}
			id="checkbox-hwgp9s2"
		>
			{formatSnakeCaseToTitle($format(setting))}
		</Checkbox>

		<TimeWheel
			id="timewheel-nw74ax3"
			a11yProps={{
				inputAriaLabel: $format('label_time_wheel_input', {
					default: 'Time wheel input',
				}),
				buttonAriaLabel: $format('label_time_wheel_button', {
					default: 'Time wheel button',
				}),
				secondsColumnAriaLabel: $format('label_time_wheel_seconds_column', {
					default: 'Time wheel seconds column',
				}),
				secondPrefixAriaLabel: $format('label_time_wheel_second_prefix', {
					default: 'Time wheel second prefix',
				}),
				msColumnAriaLabel: $format('label_time_wheel_ms_column', {
					default: 'Time wheel ms column',
				}),
				msPrefixAriaLabel: $format('label_time_wheel_ms_prefix', {
					default: 'Time wheel ms prefix',
				}),
				hoursColumnAriaLabel: $format('label_time_wheel_hours_column', {
					default: 'Time wheel hours column',
				}),
				hourPrefixAriaLabel: $format('label_time_wheel_hour_prefix', {
					default: 'Time wheel hour prefix',
				}),
				minutesColumnAriaLabel: $format('label_time_wheel_minutes_column', {
					default: 'Time wheel minutes column',
				}),
				minutePrefixAriaLabel: $format('label_time_wheel_minute_prefix', {
					default: 'Time wheel minute prefix',
				}),
			}}
			class="mb-s"
			bind:selectedHour={selectedHour[`${setting}_delay`]}
			bind:selectedMinute={selectedMinute[`${setting}_delay`]}
			bind:selectedSecond={selectedSecond[`${setting}_delay`]}
			bind:selectedMs={selectedMs[`${setting}_delay`]}
			maxHours={maxTimes.hours}
			msToggleText={getTimeWheelToggleText()}
			info={infoText}
			error={errors[`${setting}_error`]}
			texts={getTimeWheelTexts()}
			msStep={getTimeWheelMsStep(device.type)}
		/>

		<Button
			loading={loading[setting]}
			disabled={loading[setting] || errors[`${setting}_error`]}
			size="m"
			width="full"
			on:click={() => set_auto(setting)}>{$format('button_save')}</Button
		>
	</Container>
{/each}
